import React, { useState, useEffect } from 'react';
import { useApp } from '@/contexts/AppContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Clock, Gift, X, Loader2, User, Phone, Mail, Award, QrCode, AlertTriangle } from 'lucide-react';
import { formatPhoneNumber } from '@/lib/utils';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import toast from 'react-hot-toast';
import { supabase } from '@/lib/supabase';

interface CustomerDetailsProps {
  customerId: string;
  onClose: () => void;
}

const CustomerDetails: React.FC<CustomerDetailsProps> = ({ customerId, onClose }) => {
  const { 
    getCustomerById, 
    getCustomerRewards, 
    addCustomerVisit,
    redeemReward 
  } = useApp();

  const [isRecordingVisit, setIsRecordingVisit] = useState(false);
  const [showQrDialog, setShowQrDialog] = useState(false);
  const [hasVisitedToday, setHasVisitedToday] = useState(false);
  const [isCheckingVisit, setIsCheckingVisit] = useState(true);
  const customer = getCustomerById(customerId);
  const availableRewards = getCustomerRewards(customerId);

  // Check if customer has already visited today
  useEffect(() => {
    const checkTodayVisit = async () => {
      if (!customerId) return;
      
      setIsCheckingVisit(true);
      try {
        // Get today's date in YYYY-MM-DD format
        const today = new Date();
        const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate()).toISOString();
        const todayEnd = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1).toISOString();
        
        console.log(`Checking for visits between ${todayStart} and ${todayEnd}`);
        
        // Check if there's a visit record for today
        const { data, error } = await supabase
          .from('visits')
          .select('id')
          .eq('customer_id', customerId)
          .gte('date', todayStart)
          .lt('date', todayEnd);
        
        if (error) {
          console.error('Error checking today visit:', error);
          return;
        }
        
        // If there are any visits today, set hasVisitedToday to true
        setHasVisitedToday(data && data.length > 0);
        console.log(`Customer has ${data && data.length > 0 ? '' : 'not '}visited today`);
      } catch (error) {
        console.error('Error checking visit:', error);
      } finally {
        setIsCheckingVisit(false);
      }
    };
    
    checkTodayVisit();
  }, [customerId]);

  if (!customer) return null;

  // Group rewards by ID and count how many of each type
  const groupedRewards = availableRewards.reduce((acc, reward) => {
    if (!acc[reward.id]) {
      acc[reward.id] = {
        ...reward,
        count: 0
      };
    }
    acc[reward.id].count++;
    return acc;
  }, {} as Record<string, typeof availableRewards[0] & { count: number }>);

  const handleAddVisit = async () => {
    // Don't allow recording a visit if they've already visited today
    if (hasVisitedToday) {
      toast.error("This customer has already visited today");
      return;
    }
    
    setIsRecordingVisit(true);
    try {
      const success = await addCustomerVisit(customerId);
      if (success) {
        toast.success("Visit recorded successfully");
        // Update local state to reflect the new visit
        setHasVisitedToday(true);
      } else {
        toast.error("Failed to record visit");
      }
    } catch (error) {
      console.error("Error recording visit:", error);
      toast.error("Error recording visit");
    } finally {
      setIsRecordingVisit(false);
    }
  };

  const handleRedeemReward = (rewardId: string) => {
    redeemReward(customerId, rewardId);
  };

  return (
    <div className="space-y-6">
      {/* Customer Info Header */}
      <div className="bg-gray-50 p-4 rounded-lg border border-gray-100 flex flex-col sm:flex-row justify-between gap-4">
        <div className="space-y-3">
          <h2 className="text-xl sm:text-2xl font-bold flex items-center text-restaurant-secondary">
            <User className="mr-2 h-5 sm:h-6 w-5 sm:w-6 text-restaurant-primary" />
            {customer.name || "Unnamed Customer"}
          </h2>
          
          <div className="flex flex-col space-y-2">
            <div className="flex items-center text-gray-600">
              <Phone className="mr-2 h-4 w-4 text-restaurant-primary" />
              <span>{formatPhoneNumber(customer.phoneNumber)}</span>
            </div>
            
            {customer.email && (
              <div className="flex items-center text-gray-600">
                <Mail className="mr-2 h-4 w-4 text-restaurant-primary" />
                <span>{customer.email}</span>
              </div>
            )}
            
            <div className="flex items-center text-gray-600">
              <Award className="mr-2 h-4 w-4 text-restaurant-primary" />
              <span>Total Visits: {customer.visits.length}</span>
            </div>
          </div>
        </div>
        
        <div className="flex items-center justify-start sm:justify-end gap-2 mt-3 sm:mt-0">
          <Button 
            onClick={handleAddVisit}
            className={`${hasVisitedToday ? 'bg-yellow-500 hover:bg-yellow-600' : 'bg-restaurant-primary hover:bg-restaurant-primary-hover'} flex-grow sm:flex-grow-0`}
            disabled={isRecordingVisit || isCheckingVisit}
          >
            {isRecordingVisit ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : isCheckingVisit ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : hasVisitedToday ? (
              <AlertTriangle className="mr-2 h-4 w-4" />
            ) : (
              <Clock className="mr-2 h-4 w-4" />
            )}
            {isRecordingVisit ? "Recording..." : isCheckingVisit ? "Checking..." : hasVisitedToday ? "Already Visited Today" : "Record Visit"}
          </Button>
          
          <Button 
            onClick={() => setShowQrDialog(true)}
            variant="outline"
            className="border-restaurant-primary text-restaurant-primary hover:bg-restaurant-primary/10 flex-grow sm:flex-grow-0"
          >
            <QrCode className="mr-2 h-4 w-4" />
            Show QR
          </Button>
          
          <Button 
            variant="outline" 
            onClick={onClose}
            className="border-restaurant-primary text-restaurant-primary hover:bg-restaurant-primary/10"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* QR Code Dialog */}
      <Dialog open={showQrDialog} onOpenChange={setShowQrDialog}>
        <DialogContent className="sm:max-w-md mx-4 sm:mx-auto max-w-[95vw]">
          <DialogHeader>
            <DialogTitle>Customer QR Code</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col items-center justify-center p-4 sm:p-6">
            <div className="bg-white p-4 rounded-lg shadow-md">
              <img 
                src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=dinetrack:${customerId}`}
                alt="Customer QR Code"
                width={200}
                height={200}
                className="w-full max-w-[200px] h-auto"
              />
            </div>
            <p className="mt-4 text-sm text-center text-muted-foreground">
              Customer can scan this QR code to check in
            </p>
          </div>
        </DialogContent>
      </Dialog>

      <div className="grid gap-6 sm:grid-cols-2">
        <Card className="border-t-4 border-t-restaurant-primary shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
              <Clock className="h-5 w-5 text-restaurant-primary" />
              Visit History
            </CardTitle>
          </CardHeader>
          <CardContent>
            {customer.visits.length === 0 ? (
              <div className="text-center py-8 bg-gray-50 rounded-lg">
                <Clock className="h-10 w-10 text-gray-300 mx-auto mb-2" />
                <p className="text-muted-foreground">
                  No visits recorded yet
                </p>
              </div>
            ) : (
              <div className="space-y-2 max-h-[250px] sm:max-h-[300px] overflow-y-auto pr-2">
                {customer.visits.slice().reverse().map(visit => (
                  <div 
                    key={visit.id}
                    className="p-3 rounded-lg bg-gray-50 border border-gray-100 flex justify-between items-center hover:bg-gray-100 transition-colors"
                  >
                    <span className="text-sm font-medium">
                      {new Date(visit.date).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                      })}
                    </span>
                    <span className="text-xs text-gray-500">
                      {new Date(visit.date).toLocaleTimeString('en-US', {
                        hour: 'numeric',
                        minute: '2-digit'
                      })}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border-t-4 border-t-restaurant-primary shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Gift className="h-5 w-5 text-restaurant-primary" />
              Available Rewards
            </CardTitle>
          </CardHeader>
          <CardContent>
            {availableRewards.length === 0 ? (
              <div className="text-center py-8 bg-gray-50 rounded-lg">
                <Gift className="h-10 w-10 text-gray-300 mx-auto mb-2" />
                <p className="text-muted-foreground">
                  No rewards available
                </p>
              </div>
            ) : (
              <div className="space-y-4 max-h-[300px] overflow-y-auto pr-2">
                {Object.values(groupedRewards).map(reward => (
                  <div 
                    key={reward.id}
                    className="p-4 rounded-lg border border-gray-200 bg-white shadow-sm hover:shadow-md transition-shadow"
                  >
                    <div className="flex justify-between items-start gap-4">
                      <div>
                        <h3 className="font-semibold text-lg flex items-center">
                          {reward.name}
                          {reward.count > 1 && (
                            <span className="ml-2 bg-restaurant-primary text-white text-xs px-2 py-0.5 rounded-full">
                              x{reward.count}
                            </span>
                          )}
                        </h3>
                        <p className="text-sm text-muted-foreground mt-1">
                          {reward.description}
                        </p>
                      </div>
                      <Button
                        onClick={() => handleRedeemReward(reward.id)}
                        className="shrink-0 bg-restaurant-primary hover:bg-restaurant-primary-hover"
                      >
                        <Gift className="mr-2 h-4 w-4" />
                        Redeem
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CustomerDetails; 